import {
  Box,
  Chip,
  Typography,
  Grid,
  Skeleton,
  Modal,
  Button
} from "@mui/material";
import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from 'prop-types';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import amharicFont from '../../../utils/nyala.ttf';
import axiosInstance from "../../../api";
import SERVER_API_URL from "../../../config";

export default function File({ file, activeTreeItem }) {
  const [actualFile, setActualFile] = useState();
  const [fileSize, setFileSize] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const activeElementRef = useRef(null);

  useEffect(() => {
    if (activeElementRef.current) {
      activeElementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeElementRef]);

  useEffect(() => {

    const fileSizeKB = file.size / 1024;

    if (fileSizeKB < 1024) {
      setFileSize(`${fileSizeKB.toFixed(2)} KB`);
    } else {
      const fileSizeMB = fileSizeKB / 1024;
      setFileSize(`${(fileSizeMB).toFixed(2)} MB`);
    }

    if (fileSizeKB >= 1024) {
      const fileSizeMB = fileSizeKB / 1024;
      setFileSize(`${(fileSizeMB).toFixed(2)} MB`);
      if (fileSizeKB / 1024 >= 1024) {
        const fileSizeGB = fileSizeKB / 1024 / 1024;
        setFileSize(`${(fileSizeGB).toFixed(2)} GB`);
      }
    }
  }, [file.size]);

  const getFile = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${SERVER_API_URL}/chatFile/findOneFileById/${file.id}`,
        {
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        console.log('get success')
        const fileBlob = new Blob([response.data]);
        setActualFile(fileBlob);
        // setOpen(true);
      }
    } catch (e) {
      // console.log('object');
    }
  }, [file]);

  const handleDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${SERVER_API_URL}/chatFile/downloadFileById/${file.id}`,
        {
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const fileBlob = new Blob([response.data]);
        const downloadUrl = URL.createObjectURL(fileBlob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = file.originalname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      // console.error("Error downloading file:", e);
    }
  };

  const fileType = file.mimetype.split("/")[0];

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  useEffect(() => {
    getFile();
  }, [getFile])

  return (
    <Box sx={{ width: '100%' }}>

      <Box
        ref={activeTreeItem === file.id ? activeElementRef : null}
        sx={{
          cursor: "pointer",
          borderBottom: "1px solid",
          borderBottomColor: "action.selected",
          py: 2,
          width: '100%'
        }}
        onClick={handleDownload}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body1"
              color="text.primary"
              style={{ fontFamily: `'AmharicFont', ${amharicFont}, sans-serif` }}
            >
              {file.originalname}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ alignContent: 'right' }}>
            <Chip
              label={(<Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', gap: 1 }}>
                <CloudDownloadIcon />
                <Typography>አውርድ  ({fileSize})</Typography>
              </Box>)}
              size="small"
              color="info"
              sx={{ p: 1, mh: 2, alignSelf: 'right', alignContent: 'right' }}
            />
          </Grid>
        </Grid>
      </Box>

      {!actualFile && (
        <Box sx={{ py: 2, width: '100%' }}>
          {fileType === "video" && (
            <Skeleton variant="rectangular" width="100%" height={200} />
          )}
          {fileType === "image" && (
            <Skeleton variant="rectangular" width="100%" height={200} />
          )}
          {fileType === "audio" && (
            <Skeleton variant="rectangular" width="100%" height={30} />
          )}
        </Box>
      )}

      {fileType === "video" && actualFile && (
        <video controls style={{ maxWidth: "100%", maxHeight: 200 }}>
          <track kind="captions" srcLang="en" label="English captions" />
          <source src={URL.createObjectURL(actualFile)} type="video/mp4" />
          <source src={URL.createObjectURL(actualFile)} type="video/webm" />
        </video>
      )}

      {fileType === "image" && actualFile && (
        <>
          <Button onClick={handleOpenModal} sx={{ p: 0, m: 0, border: 'none', backgroundColor: 'transparent' }}>
            <img
              src={URL.createObjectURL(actualFile)}
              alt={file.originalname}
              style={{ maxWidth: "100%", maxHeight: 200, cursor: 'pointer' }}
            />
          </Button>
          <Modal open={openModal} onClose={handleCloseModal}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                outline: 'none'
              }}
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  handleCloseModal();
                }
              }}
            >
              <img
                src={URL.createObjectURL(actualFile)}
                alt={file.originalname}
                style={{ maxWidth: "90%", maxHeight: "90%" }}
              />
            </Box>
          </Modal>

        </>
      )}

      {fileType === "audio" && actualFile && (
        <audio controls style={{ width: "100%" }}>
          <track kind="captions" srcLang="en" label="English captions" />
          <source src={URL.createObjectURL(actualFile)} type="audio/mp3" />
          <source src={URL.createObjectURL(actualFile)} type="audio/mpeg" />
          <source src={URL.createObjectURL(actualFile)} type="audio/wav" />
        </audio>
      )}

    </Box>
  );
}

File.propTypes = {
  file: PropTypes.shape({
    mimetype: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    originalname: PropTypes.string.isRequired,
  }).isRequired,
  activeTreeItem: PropTypes.string.isRequired,
};
